import * as THREE from "three";
import { Soup } from "./kit";

// Modelling moves on top of the kit (kit.ts), from the superposition spike's props: every part goes into one Soup as
// tagged triangles, and the model is painted at the end per face (Soup.repaint: by position, normal and tag), after
// any Soup.bisect cuts, then smoothed under an angle (Soup.geometry). One model, one vertex-coloured mesh.

type V3 = THREE.Vector3;
const V = (x: number, y: number, z: number): V3 => new THREE.Vector3(x, y, z);
const GREY = new THREE.Color(0.5, 0.5, 0.5);

/** Every triangle of a three.js geometry, moved by `m`, into the soup with `tag`. */
export function addGeo(s: Soup, geo: THREE.BufferGeometry, tag: number, m?: THREE.Matrix4): void {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (m) g.applyMatrix4(m);
  const P = g.getAttribute("position");
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  for (let i = 0; i < P.count; i += 3) {
    a.fromBufferAttribute(P, i);
    b.fromBufferAttribute(P, i + 1);
    c.fromBufferAttribute(P, i + 2);
    s.tri(a, b, c, GREY, tag);
  }
}

/** An axis-aligned box (12 triangles). */
export function box(s: Soup, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, tag: number): void {
  const g = new THREE.BoxGeometry(x1 - x0, y1 - y0, z1 - z0);
  g.translate((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
  addGeo(s, g, tag);
}

/** Appends `src`'s triangles to `dst`, moved by `m` (colours and tags kept). */
export function append(dst: Soup, src: Soup, m: THREE.Matrix4): void {
  const v = new THREE.Vector3();
  for (let i = 0; i < src.p.length; i += 3) {
    v.set(src.p[i]!, src.p[i + 1]!, src.p[i + 2]!).applyMatrix4(m);
    dst.p.push(v.x, v.y, v.z);
  }
  dst.c.push(...src.c);
  dst.t.push(...src.t);
}

/**
 * A swept tube through `pts` (a Catmull-Rom curve, `n` rings), its section an ellipse of half widths
 * `r(t) * [sx, sy]` in the curve's frame (sx along `side`, a fixed vector roughly across the curve's plane), with
 * `seg` sides; `caps` closes the ends with fans.
 */
export function tube(
  s: Soup,
  pts: V3[],
  r: (t: number) => number,
  o: { n: number; seg: number; tag: number; side?: V3; sx?: number; sy?: number; caps?: boolean; closed?: boolean },
): void {
  const curve = new THREE.CatmullRomCurve3(pts, o.closed ?? false, "centripetal");
  const side0 = (o.side ?? V(1, 0, 0)).clone().normalize();
  const rings: V3[][] = [];
  for (let i = 0; i <= o.n; i++) {
    const t = i / o.n;
    const p = curve.getPointAt(t);
    const tan = curve.getTangentAt(t).normalize();
    // the section's axes: `side` made perpendicular to the tangent, and the second axis across both
    const ax = side0.clone().addScaledVector(tan, -side0.dot(tan));
    if (ax.lengthSq() < 1e-8) ax.set(0, 1, 0).addScaledVector(tan, -tan.y);
    ax.normalize();
    const ay = new THREE.Vector3().crossVectors(tan, ax).normalize();
    const rr = r(t);
    const ring: V3[] = [];
    for (let k = 0; k < o.seg; k++) {
      const th = (2 * Math.PI * k) / o.seg;
      ring.push(p.clone().addScaledVector(ax, Math.cos(th) * rr * (o.sx ?? 1)).addScaledVector(ay, Math.sin(th) * rr * (o.sy ?? 1)));
    }
    rings.push(ring);
  }
  for (let i = 0; i < rings.length - 1; i++) {
    const A = rings[i]!;
    const B = rings[i + 1]!;
    for (let k = 0; k < o.seg; k++) {
      const k1 = (k + 1) % o.seg;
      s.tri(A[k]!, A[k1]!, B[k1]!, GREY, o.tag);
      s.tri(A[k]!, B[k1]!, B[k]!, GREY, o.tag);
    }
  }
  if (o.caps ?? true) {
    const fan = (ring: V3[], flip: boolean): void => {
      const c = ring.reduce((a, p) => a.add(p), new THREE.Vector3()).multiplyScalar(1 / ring.length);
      for (let k = 0; k < ring.length; k++) {
        const k1 = (k + 1) % ring.length;
        if (flip) s.tri(c, ring[k1]!, ring[k]!, GREY, o.tag);
        else s.tri(c, ring[k]!, ring[k1]!, GREY, o.tag);
      }
    };
    fan(rings[0]!, true); // (the start cap faces back along the curve)
    fan(rings[rings.length - 1]!, false);
  }
}

/**
 * A closed body of revolution-like rings: `rings(v)` gives, for v in [0, 1] bottom to top, the ring's centre and
 * per-angle radius (th from +X toward +Z); the ends close to poles at the first and last centres.
 */
export function body(
  s: Soup,
  nv: number,
  seg: number,
  tag: number,
  ringAt: (v: number) => { c: V3; r: (th: number) => [number, number, number] },
): void {
  const rings: V3[][] = [];
  const centres: V3[] = [];
  for (let j = 0; j <= nv; j++) {
    const { c, r } = ringAt(j / nv);
    centres.push(c);
    const ring: V3[] = [];
    for (let k = 0; k < seg; k++) {
      const th = (2 * Math.PI * k) / seg;
      const [dx, dy, dz] = r(th);
      ring.push(V(c.x + dx, c.y + dy, c.z + dz));
    }
    rings.push(ring);
  }
  for (let j = 0; j < nv; j++) {
    const A = rings[j]!;
    const B = rings[j + 1]!;
    for (let k = 0; k < seg; k++) {
      const k1 = (k + 1) % seg;
      s.tri(A[k]!, B[k]!, B[k1]!, GREY, tag);
      s.tri(A[k]!, B[k1]!, A[k1]!, GREY, tag);
    }
  }
  const lo = rings[0]!;
  const hi = rings[nv]!;
  for (let k = 0; k < seg; k++) {
    const k1 = (k + 1) % seg;
    s.tri(centres[0]!, lo[k]!, lo[k1]!, GREY, tag);
    s.tri(centres[nv]!, hi[k1]!, hi[k]!, GREY, tag);
  }
}

/** An ellipsoid (radii r) at c. */
export function ellipsoid(s: Soup, c: V3, r: V3, tag: number, seg = 14, nv = 9): void {
  body(s, nv, seg, tag, (v) => {
    const phi = -Math.PI / 2 + Math.PI * THREE.MathUtils.clamp(v, 0.02, 0.98);
    const k = Math.cos(phi);
    return { c: V(c.x, c.y + Math.sin(phi) * r.y, c.z), r: (th) => [Math.cos(th) * r.x * k, 0, Math.sin(th) * r.z * k] };
  });
}

/** A cheap 2D hash in [0, 1). */
export function hash(x: number, y: number): number {
  const h = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return h - Math.floor(h);
}

/** Smooth value noise in [0, 1). */
export function vnoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = x - xi;
  const fy = y - yi;
  const u = fx * fx * (3 - 2 * fx);
  const v = fy * fy * (3 - 2 * fy);
  const a = hash(xi, yi);
  const b = hash(xi + 1, yi);
  const c = hash(xi, yi + 1);
  const d = hash(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

/** A Vector3 from components. */
export { V };
